<template>
  <div>
    <input ref="inputVertical" type="file" class="d-none" accept="video/*" @change="enviarArquivo">
    <input ref="inputHorizontal" type="file" class="d-none" accept="video/*" @change="enviarArquivo">

    <!-- Trigger Button -->
    <div id="tour-step-upload-btn">
      <el-button size="small" class="btnpersonalizado" type="danger" data-v-step="1" @click="openUploadModal">
        <feather-icon icon="UploadCloudIcon" stroke="white" />
        Upload
      </el-button>
    </div>

    <!-- ─── Upload Modal ─── -->
    <transition name="uv-fade">
      <div v-if="uploadModalVisible" class="uvm-overlay" @click.self="!isUploading && closeUploadModal()">
        <div class="uvm-modal">

          <!-- Modal Header -->
          <div class="uvm-header">
            <div class="uvm-header-icon">
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                <polyline points="16 16 12 12 8 16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <line x1="12" y1="12" x2="12" y2="21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </div>
            <div>
              <div class="uvm-header-title">Upload de Vídeo</div>
              <div class="uvm-header-sub">Selecione um ou dois formatos para upload</div>
            </div>
            <button v-if="!isUploading" class="uvm-close" @click="closeUploadModal">
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24">
                <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              </svg>
            </button>
          </div>

          <!-- ─── Upload Cards (idle state) ─── -->
          <div v-if="!isUploading" class="uvm-body">

            <div class="uvm-cards">

              <!-- Card Vertical -->
              <div
                class="uvm-card"
                :class="{
                  'uvm-card-selected': selectedFiles.vertical,
                  'uvm-card-drag': dragging === 'vertical'
                }"
                @click="openFileDialog('vertical')"
                @dragover.prevent="dragging = 'vertical'"
                @dragleave="dragging = null"
                @drop.prevent="onDrop($event, 'vertical')"
              >
                <div class="uvm-card-icon" :class="selectedFiles.vertical ? 'uvm-icon-ok' : 'uvm-icon-idle'">
                  <svg v-if="!selectedFiles.vertical" width="32" height="32" fill="none" viewBox="0 0 24 24">
                    <rect x="5" y="2" width="14" height="20" rx="2" stroke="currentColor" stroke-width="1.5"/>
                    <circle cx="12" cy="15" r="1.5" fill="currentColor"/>
                  </svg>
                  <svg v-else width="32" height="32" fill="none" viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </div>

                <div class="uvm-card-label">Vídeo Vertical</div>
                <div class="uvm-card-hint">9:16 · Ideal para mobile</div>

                <div v-if="selectedFiles.vertical" class="uvm-file-info">
                  <div class="uvm-file-name">{{ selectedFiles.vertical.name }}</div>
                  <div class="uvm-file-size">{{ formatSize(selectedFiles.vertical.size) }}</div>
                </div>

                <div class="uvm-card-drop-hint" v-if="!selectedFiles.vertical">
                  Arraste ou clique para selecionar
                </div>

                <div class="uvm-card-btn" :class="selectedFiles.vertical ? 'uvm-btn-change' : 'uvm-btn-select'">
                  {{ selectedFiles.vertical ? 'Trocar arquivo' : 'Selecionar arquivo' }}
                </div>
              </div>

              <!-- Card Horizontal -->
              <div
                class="uvm-card"
                :class="{
                  'uvm-card-selected': selectedFiles.horizontal,
                  'uvm-card-drag': dragging === 'horizontal'
                }"
                @click="openFileDialog('horizontal')"
                @dragover.prevent="dragging = 'horizontal'"
                @dragleave="dragging = null"
                @drop.prevent="onDrop($event, 'horizontal')"
              >
                <div class="uvm-card-icon" :class="selectedFiles.horizontal ? 'uvm-icon-ok' : 'uvm-icon-idle'">
                  <svg v-if="!selectedFiles.horizontal" width="32" height="32" fill="none" viewBox="0 0 24 24">
                    <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.5"/>
                    <line x1="8" y1="21" x2="16" y2="21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                    <line x1="12" y1="19" x2="12" y2="21" stroke="currentColor" stroke-width="1.5"/>
                  </svg>
                  <svg v-else width="32" height="32" fill="none" viewBox="0 0 24 24">
                    <polyline points="20 6 9 17 4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </div>

                <div class="uvm-card-label">Vídeo Horizontal</div>
                <div class="uvm-card-hint">16:9 · Ideal para desktop</div>

                <div v-if="selectedFiles.horizontal" class="uvm-file-info">
                  <div class="uvm-file-name">{{ selectedFiles.horizontal.name }}</div>
                  <div class="uvm-file-size">{{ formatSize(selectedFiles.horizontal.size) }}</div>
                </div>

                <div class="uvm-card-drop-hint" v-if="!selectedFiles.horizontal">
                  Arraste ou clique para selecionar
                </div>

                <div class="uvm-card-btn" :class="selectedFiles.horizontal ? 'uvm-btn-change' : 'uvm-btn-select'">
                  {{ selectedFiles.horizontal ? 'Trocar arquivo' : 'Selecionar arquivo' }}
                </div>
              </div>
            </div>

            <!-- Footer -->
            <div class="uvm-footer">
              <button class="uvm-btn uvm-btn-cancel" @click="closeUploadModal">Cancelar</button>
              <button
                class="uvm-btn uvm-btn-upload"
                :disabled="selectedFilesCount === 0"
                :class="{ 'uvm-btn-disabled': selectedFilesCount === 0 }"
                @click="uploadSelectedFiles"
              >
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                  <polyline points="16 16 12 12 8 16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  <line x1="12" y1="12" x2="12" y2="21" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
                Fazer Upload
                <span v-if="selectedFilesCount > 0" class="uvm-count-badge">{{ selectedFilesCount }}</span>
              </button>
            </div>
          </div>

          <!-- ─── Uploading State ─── -->
          <div v-else class="uvm-uploading">
            <div class="uvm-uploading-inner">

              <!-- Animated Icon -->
              <div class="uvm-upload-anim">
                <div class="uvm-upload-ring"></div>
                <svg width="28" height="28" fill="none" viewBox="0 0 24 24" class="uvm-upload-arrow">
                  <polyline points="16 16 12 12 8 16" stroke="#E86A10" stroke-width="2.5" stroke-linecap="round"/>
                  <line x1="12" y1="12" x2="12" y2="21" stroke="#E86A10" stroke-width="2.5" stroke-linecap="round"/>
                  <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3" stroke="#E86A10" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </div>

              <!-- Current file info -->
              <div class="uvm-upload-status">
                <div class="uvm-upload-title">{{ uploadingLabel }}</div>
                <div class="uvm-upload-file">{{ uploadingFile }}</div>
              </div>

              <!-- Progress Bar -->
              <div class="uvm-progress-wrap">
                <div class="uvm-progress-track">
                  <div class="uvm-progress-fill" :style="{ width: uploadPercentage + '%' }"></div>
                </div>
                <div class="uvm-progress-meta">
                  <span class="uvm-progress-pct">{{ uploadPercentage }}%</span>
                  <span class="uvm-progress-eta">{{ uploadPercentage < 100 ? 'Enviando…' : 'Processando…' }}</span>
                </div>
              </div>

              <!-- Steps -->
              <div class="uvm-steps">
                <div class="uvm-step" :class="uploadPercentage > 0 ? 'uvm-step-done' : 'uvm-step-active'">
                  <div class="uvm-step-dot">
                    <svg v-if="uploadPercentage > 0" width="10" height="10" fill="none" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>
                  </div>
                  <span>Conectando ao servidor</span>
                </div>
                <div class="uvm-step" :class="uploadPercentage === 100 ? 'uvm-step-done' : uploadPercentage > 0 ? 'uvm-step-active' : ''">
                  <div class="uvm-step-dot">
                    <svg v-if="uploadPercentage === 100" width="10" height="10" fill="none" viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>
                  </div>
                  <span>Enviando vídeo</span>
                </div>
                <div class="uvm-step" :class="uploadPercentage === 100 ? 'uvm-step-active' : ''">
                  <div class="uvm-step-dot"></div>
                  <span>Gerando embed e configurações</span>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import { mapActions, mapState } from 'vuex'
import { GetFrameVideoUrl } from '@/services/getFrameVideoUrl'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { UploadVideoService } from '@/services/uploadVideo'
import Ripple from 'vue-ripple-directive'

export default {
  name: 'Upload',
  components: { ToastificationContent },
  directives: { Ripple },

  data() {
    return {
      statusConversion: '',
      conversionStatus: '',
      conversionProgress: 0,
      notificationStatus: null,
      loading: false,
      loadingts: 20000,
      caminhoPasta: '',
      VUE_APP_HOST_API_AWS_S3: process.env.VUE_APP_HOST_API_AWS_S3 || "/api/dkbucket",
      VUE_APP_HOST_API_BACKEND: process.env.VUE_APP_HOST_API_BACKEND || "/api/dkbackend",
      userData: JSON.parse(localStorage.getItem('userData')),
      folderData: JSON.parse(localStorage.getItem('folderData')),
      folderATUAL: {},
      form: { files: '' },
      uploadModalVisible: false,
      currentUploadType: null,
      selectedFiles: { vertical: null, horizontal: null },
      file: '',
      notification: null,
      notificationStatus: null,
      uploadPercentage: 0,
      isUploading: false,
      dragging: null,
      uploadingLabel: 'Enviando vídeo…',
      uploadingFile: '',
      vsl: {
        vsl_name: 'De um nome ao video',
        vsl_description: 'Sem Descrição',
        vsl_tag: 'Sem tag',
        id_user: '5',
        id_folder: '0',
      },
      estilo: {
        id_vsl: '45',
        controls_bt_big_player: '1',
        controls_bt_small_player: '1',
        controls_progress_bar: '1',
        controls_video_time: '1',
        controls_video_vol: '1',
        controls_bt_screem: '1',
        controls_avan_10s: '1',
        controls_retro_10s: '1',
        controls_back_color: '#ff0000',
        controls_main_color: '#ff0000',
      },
      Autoplay: {
        autoplayer_text_color: '#f5f5f5',
        autoplayer_back_color: '#ad1414',
        autoplayer_top_text: 'Seu vídeo já começou',
        autoplayer_button_text: 'Clique para ouvir',
      },
      ProgressoInt: { bar_hight: 10, bar_cor: '#de1717' },
      Viewer: {
        viewer_message: 'Você já começou a assistir esse vídeo, o que deseja fazer? ',
        viewer_bt_restart: 'Recomeçar',
        viewer_bt_continue: 'Continuar assistindo',
        viewer_back_color: '#f60909',
        viewer_color_text: '#f1f2f2',
        id_vsl: 182,
      },
      thumb: {
        tumb_start: '',
        tumb_pause: '',
        tumb_end: '',
        tumb_ativo: 0,
        tumb_key_name: '',
      },
      vslTempoVideoSemFormat: '',
      errorMessage: null,
      vslTempoVideo: '',
      UrlVideos: '',
      Hashfolder: '',
      NameVideos: '',
      key_name: '',
      IdVsl: '',
      frameCurrentVsl: {},
    }
  },

  computed: {
    ...mapState('Folders', ['folderAtual']),
    hasSelectedFiles() {
      return this.selectedFiles.vertical || this.selectedFiles.horizontal
    },
    selectedFilesCount() {
      let count = 0
      if (this.selectedFiles.vertical) count += 1
      if (this.selectedFiles.horizontal) count += 1
      return count
    },
  },

  mounted() {
    this.listarFolders()
  },

  methods: {
    ...mapActions('Videos', ['listarVideos']),
    ...mapActions('Folders', ['listarFolders']),

    formatSize(bytes) {
      if (!bytes) return ''
      const mb = bytes / (1024 * 1024)
      return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`
    },

    onDrop(event, type) {
      this.dragging = null
      const file = event.dataTransfer.files[0]
      if (file && file.type.startsWith('video/')) {
        this.currentUploadType = type
        this.selectedFiles[type] = file
      }
    },

    open() {
      this.notification = this.$notify({
        title: 'Fazendo upload…',
        dangerouslyUseHTMLString: true,
        message: `<strong>${this.uploadPercentage}%</strong> enviado`,
        position: 'bottom-right',
        duration: 0,
      })
    },
    close() {
      if (this.notification) {
        this.notification.close()
        this.notification = null
      }
    },
    closeStatusConversao() {
      if (this.notificationStatus) {
        this.notificationStatus.close()
        this.notificationStatus = null
      }
    },

    openUploadModal() {
      this.uploadModalVisible = true
      this.idfolderAtual()
    },

    closeUploadModal() {
      this.uploadModalVisible = false
      this.currentUploadType = null
      this.selectedFiles = { vertical: null, horizontal: null }
      this.isUploading = false
      this.uploadPercentage = 0
    },

    openFileDialog(type) {
      if (!type) {
        this.$refs.inputVertical.value = null
        this.$refs.inputVertical.click()
        return
      }
      this.currentUploadType = type
      if (type === 'vertical') {
        this.$refs.inputVertical.value = null
        this.$refs.inputVertical.click()
      } else if (type === 'horizontal') {
        this.$refs.inputHorizontal.value = null
        this.$refs.inputHorizontal.click()
      }
    },

    async uploadSelectedFiles() {
      this.isUploading = true
      this.uploadPercentage = 0

      if (this.selectedFiles.vertical) {
        this.uploadingLabel = 'Enviando vídeo vertical…'
        this.uploadingFile = this.selectedFiles.vertical.name
        await this.uploadSingleFile(this.selectedFiles.vertical, 'vertical')
      }
      if (this.selectedFiles.horizontal) {
        this.uploadingLabel = 'Enviando vídeo horizontal…'
        this.uploadingFile = this.selectedFiles.horizontal.name
        this.uploadPercentage = 0
        await this.uploadSingleFile(this.selectedFiles.horizontal, 'horizontal')
      }

      this.closeUploadModal()
      this.$message({ type: 'success', message: 'Todos os uploads foram concluídos com sucesso' })
    },

    clearFolder() {
      this.$store.commit('Folders/setfolderAtual', {
        id: 0, folder_name: null, folder_backet_adress: null,
        id_main: null, id_user: null, folder_date: null, folder_key_name: null,
      })
    },

    async idfolderAtual() {
      this.folderATUAL = JSON.parse(localStorage.getItem('folderATUAL'))
    },

    async enviarArquivo(e) {
      const file = e.target.files[0]
      if (!file) return
      if (this.currentUploadType) {
        this.selectedFiles[this.currentUploadType] = file
      } else {
        await this.uploadSingleFile(file, 'vertical')
      }
    },

    normalizeFileName(fileName) {
      return fileName
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9._-]/g, '_')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .toLowerCase()
    },

    /* Lê a duração do vídeo localmente via HTML5 (sem passar pelo servidor) */
    _getVideoDuration(file) {
      return new Promise(resolve => {
        const video = document.createElement('video')
        video.preload = 'metadata'
        video.onloadedmetadata = () => { URL.revokeObjectURL(video.src); resolve(video.duration || 0) }
        video.onerror = () => resolve(0)
        video.src = URL.createObjectURL(file)
      })
    },

    /* Upload direto browser→S3 via URL pré-assinada (bypassa Cloudflare) */
    _uploadToS3Direct(presignedUrl, file) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhr.open('PUT', presignedUrl)
        xhr.setRequestHeader('Content-Type', file.type)
        xhr.upload.onprogress = e => {
          if (e.lengthComputable) {
            this.uploadPercentage = Math.round((e.loaded / e.total) * 100)
          }
        }
        xhr.onload  = () => (xhr.status >= 200 && xhr.status < 300) ? resolve() : reject(new Error(`S3 error ${xhr.status}`))
        xhr.onerror = () => reject(new Error('Falha na conexão com o S3'))
        xhr.send(file)
      })
    },

    async uploadSingleFile(file, videoType) {
      this.loading = true

      // Garante user_file_key válido — busca do banco se localStorage estiver desatualizado
      if (!this.userData.user_file_key) {
        try {
          const resp = await fetch(`${this.VUE_APP_HOST_API_BACKEND}/me`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` },
          })
          const me = await resp.json()
          if (me && me.user_file_key) {
            this.userData.user_file_key = me.user_file_key
            const ud = JSON.parse(localStorage.getItem('userData') || '{}')
            ud.user_file_key = me.user_file_key
            localStorage.setItem('userData', JSON.stringify(ud))
          }
        } catch {}

        // Fallback: gera localmente baseado no nome + id
        if (!this.userData.user_file_key) {
          const sanitize = s => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '')
          const slug = `${sanitize(this.userData.user_name)}_${sanitize(this.userData.user_sobrenome || '')}`.replace(/_+/g, '_').replace(/^_|_$/g, '')
          this.userData.user_file_key = `${slug}-Id${this.userData.id}/`
        }
      }

      const folderId = this.folderATUAL && typeof this.folderATUAL === 'object'
        ? (this.folderATUAL.id || 0)
        : (Number(this.folderATUAL) || 0)
      if (!folderId) {
        this.caminhoPasta = `${this.userData.user_file_key}UploadFolderVideos/`
      } else {
        this.caminhoPasta = this.folderATUAL.folder_key_name
      }

      const originalFileName = file.name
      const fileExtension = originalFileName.substring(originalFileName.lastIndexOf('.'))
      const fileNameWithoutExtension = originalFileName.substring(0, originalFileName.lastIndexOf('.'))
      const normalizedFileName = this.normalizeFileName(fileNameWithoutExtension) + fileExtension

      this.open()

      let response
      try {
        /* 1. Pegar duração do vídeo localmente */
        const duration = await this._getVideoDuration(file)

        /* 2. Solicitar URL pré-assinada ao servidor (requisição pequena) */
        const presignResp = await fetch(`${this.VUE_APP_HOST_API_AWS_S3}/presign-upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileName: normalizedFileName,
            caminho: this.caminhoPasta,
            contentType: file.type || 'video/mp4',
          }),
        })
        if (!presignResp.ok) {
          const errJson = await presignResp.json().catch(() => ({}))
          throw new Error(errJson.error || `Erro ao gerar URL de upload (${presignResp.status})`)
        }
        const { presignedUrl, key, folderName, bucket, region } = await presignResp.json()

        /* 3. Upload direto do browser para o S3 — bypassa Cloudflare */
        await this._uploadToS3Direct(presignedUrl, file)

        /* 4. Registrar o vídeo no servidor (requisição pequena) */
        const finalizeResp = await fetch(`${this.VUE_APP_HOST_API_AWS_S3}/finalize-upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ key, folderName, bucket, region, caminho: this.caminhoPasta, duration }),
        })
        if (!finalizeResp.ok) {
          const errJson = await finalizeResp.json().catch(() => ({}))
          throw new Error(errJson.error || `Erro ao finalizar upload (${finalizeResp.status})`)
        }
        response = await finalizeResp.json()
      } catch (err) {
        this.close()
        const msg = err?.message || 'Erro ao fazer upload. Verifique as credenciais AWS em Administração → AWS S3.'
        this.$message({ type: 'error', message: msg, duration: 8000 })
        console.error('Upload falhou:', err)
        this.isUploading = false
        this.uploadPercentage = 0
        return
      }

      /* A partir daqui: mesmo fluxo do código original
         Nota: fetch retorna o JSON diretamente em response.data (sem wrapper axios) */
      await (async () => {
          const d = response.data  /* { fileUrl, duration, status, ... } */
          this.vslTempoVideo = d.durationFormatted
          this.vslTempoVideoSemFormat = d.duration
          this.UrlVideos = d.fileUrl
          this.UrlVideosM3U8 = d.fileUrlm3u8
          this.NameVideos = d.fileNameOrigin
          this.key_name = d.caminhoFolder
          const paramsMidiaConvert = {
            fileKey: d.key_filename,
            bucket: d.bucket || 'dkplayer',
            vslId: this.IdVsl,
          }
          this.MidiaConvert(paramsMidiaConvert)

          if (d.status === 201) {
            this.frameCurrentVsl = {
              video_url: this.UrlVideos,
              caminho: d.caminhoFolder,
            }
            if (!this.videoGroupId) {
              this.videoGroupId = `${this.userData.id}_${Date.now()}`
            }
            const orientation = videoType === 'vertical' ? 'Vertical' : 'Horizontal'
            await fetch(`${this.VUE_APP_HOST_API_BACKEND}/vsl`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: `{"vsl_name":"${this.NameVideos}","vsl_description":"${this.vsl.vsl_description}","vsl_tag":"${this.vsl.vsl_tag}","id_user":"${this.userData.id}","id_folder":"${folderId}","vsl_bucket_address":"${this.UrlVideos}","vsl_key_name":"${this.key_name}","vsl_frame":"${this.UrlVideos}","vsl_tempoVideo":"${this.vslTempoVideo}","vsl_bucket_address_m3u8":"${this.UrlVideosM3U8}","videoGroupId":"${this.videoGroupId}","orientation":"${orientation}"}`,
            })
              .finally(() => { this.loading = false; this.loadingts = 0 })
              .then(response => response.json())
              .then(async response => {
                this.IdVsl = response.Id
                this.frameCurrentVsl.vsl_id = response.Id
                await this.listarVideos()
                this.$message({ type: 'success', message: 'Upload concluido com sucesso' })
                if (response.status === 201) {
                  fetch(`${this.VUE_APP_HOST_API_BACKEND}/control`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: `{"id_vsl":"${this.IdVsl}","controls_bt_big_player":"${this.estilo.controls_bt_big_player}","controls_bt_small_player":"${this.estilo.controls_bt_small_player}","controls_progress_bar":"${this.estilo.controls_progress_bar}","controls_video_time":"${this.estilo.controls_video_time}","controls_video_vol":"${this.estilo.controls_video_vol}","controls_bt_screem":"${this.estilo.controls_bt_screem}","controls_retro_10s":"${this.estilo.controls_retro_10s}","controls_avan_10s":"${this.estilo.controls_avan_10s}","controls_back_color":"${this.estilo.controls_back_color}","controls_main_color":"${this.estilo.controls_main_color}"}`,
                  })
                  fetch(`${this.VUE_APP_HOST_API_BACKEND}/atuvsl`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: `{"id_vsl":"${this.IdVsl}","ptime":"${this.vslTempoVideoSemFormat}","pvisitorId":"0"}`,
                  })
                  fetch(`${this.VUE_APP_HOST_API_BACKEND}/autoplayer`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: `{"id_vsl":"${this.IdVsl}","autoplayer_text_color":"${this.Autoplay.autoplayer_text_color}","autoplayer_back_color":"${this.Autoplay.autoplayer_back_color}","autoplayer_top_text":"${this.Autoplay.autoplayer_top_text}","autoplayer_button_text":"${this.Autoplay.autoplayer_button_text}"}`,
                  })
                  fetch(`${this.VUE_APP_HOST_API_BACKEND}/bar`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: `{"id_vsl":"${this.IdVsl}","bar_hight":"${this.ProgressoInt.bar_hight}","bar_cor":"${this.ProgressoInt.bar_cor}"}`,
                  })
                  fetch(`${this.VUE_APP_HOST_API_BACKEND}/viewer`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: `{"id_vsl":"${this.IdVsl}","viewer_message":"${this.Viewer.viewer_message}","viewer_bt_restart":"${this.Viewer.viewer_bt_restart}","viewer_bt_continue":"${this.Viewer.viewer_bt_continue}","viewer_back_color":"${this.Viewer.viewer_back_color}","viewer_color_text":"${this.Viewer.viewer_color_text}"}`,
                  })
                  fetch(`${this.VUE_APP_HOST_API_BACKEND}/tumb`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: `{"id_vsl":"${this.IdVsl}","tumb_start":"${this.thumb.tumb_start}","tumb_pausa":"${this.thumb.tumb_pause}","tumb_end":"${this.thumb.tumb_end}","tumb_ativo":"${this.thumb.tumb_ativo}","tumb_key_name_end":"${this.thumb.tumb_key_name}"}`,
                  })
                  fetch(`${this.VUE_APP_HOST_API_BACKEND}/analytic`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                    body: `{"id_vsl":"${this.IdVsl}"}`,
                  }).then(response => { console.log(response, 'AutoPLay') })
                }
                this.close()
                this.clearFolder()
              })
              .catch(err => console.error(err))
          }
      })()

      await new GetFrameVideoUrl().execute(this.frameCurrentVsl)
        .then(async resp => {
          await this.$api.put('/updateFrame', {
            id_vsl: this.frameCurrentVsl.vsl_id,
            vsl_frame: resp.file_url_frame,
          }).finally(() => { this.listarVideos() })
        })
        .catch(err => { console.warn('[GetFrameVideoUrl] erro ao gerar frame:', err?.message || err) })
    },

    async MidiaConvert(params) {
      const uploadVideoService = new UploadVideoService()
      await uploadVideoService.mediaConvert(params)
        .then(response => { this.MonitorarConversao(response.JobId) })
        .catch(err => console.error(err))
    },

    async MonitorarConversao(jobId) {
      if (!jobId || jobId === 'SKIP') return

      let status = ''
      let progress = 0
      this.conversionStatus = 'PROCESSING'
      this.conversionProgress = 0

      this.notificationStatus = this.$notify({
        title: 'Convertendo vídeo para HLS…',
        dangerouslyUseHTMLString: true,
        message: '<div style="color:#8a94a6;font-size:13px;">Gerando segmentos .ts para streaming otimizado.<br><strong style="color:#E86A10">0%</strong></div>',
        position: 'bottom-right',
        duration: 0,
      })

      const uploadVideoService = new UploadVideoService()
      let attempts = 0
      // Polling até 30 minutos (180 tentativas x 10s)
      while (status !== 'COMPLETE' && status !== 'ERROR' && status !== 'CANCELED' && attempts < 180) {
        attempts++
        await new Promise(resolve => setTimeout(resolve, 10000))
        try {
          const response = await uploadVideoService.monitorarConversao(jobId)
          status = response?.status || 'PROGRESSING'
          progress = response?.progress || 0
          this.conversionProgress = progress

          if (this.notificationStatus) {
            const statusLabel = {
              SUBMITTED: 'Enviado para processamento',
              PROGRESSING: 'Convertendo segmentos',
              COMPLETE: 'Concluído',
              ERROR: 'Erro na conversão',
            }[status] || status

            this.notificationStatus.message = `<div style="color:#8a94a6;font-size:13px;">${statusLabel}<br><div style="margin-top:8px;background:#e2e8f0;border-radius:6px;height:6px;overflow:hidden;"><div style="background:#E86A10;height:100%;width:${progress}%;transition:width 0.3s"></div></div><strong style="color:#E86A10;display:block;margin-top:4px">${progress}%</strong></div>`
          }
        } catch (e) {
          console.warn('[MonitorarConversao]', e)
        }
      }

      if (this.notificationStatus) this.notificationStatus.close()

      if (status === 'COMPLETE') {
        this.conversionStatus = 'COMPLETE'
        this.$notify({
          title: '✅ Vídeo pronto!',
          message: 'Conversão HLS concluída com sucesso.',
          type: 'success',
          position: 'bottom-right',
          duration: 5000,
        })
      } else if (status === 'ERROR' || status === 'CANCELED') {
        this.conversionStatus = 'ERROR'
        this.$notify({
          title: 'Erro na conversão',
          message: 'A conversão HLS falhou. O vídeo será reproduzido como MP4.',
          type: 'warning',
          position: 'bottom-right',
          duration: 8000,
        })
      } else {
        this.$notify({
          title: 'Conversão demorada',
          message: 'O vídeo continua sendo processado em segundo plano.',
          type: 'info',
          position: 'bottom-right',
          duration: 5000,
        })
      }
    },
  },

  watch: {
    uploadPercentage(newValue) {
      if (this.notification) {
        this.notification.message = `<strong>${newValue}%</strong> enviado`
      }
    },
  },
}
</script>

<style scoped>
/* ─── Transition ─── */
.uv-fade-enter-active, .uv-fade-leave-active { transition: opacity 0.2s; }
.uv-fade-enter, .uv-fade-leave-to { opacity: 0; }

/* ─── Overlay ─── */
.uvm-overlay {
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(13, 51, 73, 0.55);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999;
}

/* ─── Modal ─── */
.uvm-modal {
  background: var(--dk-bg-card, #fff); border-radius: 20px;
  width: 100%; max-width: 580px;
  box-shadow: 0 24px 60px rgba(0,0,0,0.22);
  overflow: hidden;
}

/* ─── Header ─── */
.uvm-header {
  display: flex; align-items: center; gap: 14px;
  padding: 20px 22px;
  background: linear-gradient(135deg, #0D3349 0%, #1a4d6b 100%);
  color: #fff;
}
.uvm-header-icon {
  width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0;
  background: rgba(232,106,16,0.85);
  display: flex; align-items: center; justify-content: center;
}
.uvm-header-title { font-size: 16px; font-weight: 700; }
.uvm-header-sub { font-size: 12.5px; opacity: 0.65; margin-top: 2px; }
.uvm-close {
  margin-left: auto; background: rgba(255,255,255,0.12); border: none; cursor: pointer;
  width: 34px; height: 34px; border-radius: 8px; flex-shrink: 0;
  color: #fff; display: flex; align-items: center; justify-content: center;
  transition: background 0.15s;
}
.uvm-close:hover { background: rgba(255,255,255,0.22); }

/* ─── Body ─── */
.uvm-body { padding: 22px; }

/* ─── Cards ─── */
.uvm-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 20px; }

.uvm-card {
  border: 2px dashed #dde1e8; border-radius: 14px;
  padding: 24px 16px; text-align: center; cursor: pointer;
  transition: all 0.2s; background: var(--dk-bg-card-hover, #fafbfc); position: relative;
  overflow: hidden;
}
.uvm-card:hover { border-color: #E86A10; background: #fff8f4; transform: translateY(-2px); box-shadow: 0 6px 20px rgba(232,106,16,0.12); }
.uvm-card-drag { border-color: #E86A10 !important; background: #fff3e8 !important; border-style: solid !important; }
.uvm-card-selected { border-color: #28c76f; border-style: solid; background: #f0faf5; }
.uvm-card-selected:hover { border-color: #28c76f; background: #e8f8f0; }

.uvm-card-icon {
  width: 60px; height: 60px; border-radius: 16px; margin: 0 auto 14px;
  display: flex; align-items: center; justify-content: center;
}
.uvm-icon-idle { background: rgba(232,106,16,0.1); color: #E86A10; }
.uvm-icon-ok { background: rgba(40,199,111,0.12); color: #28c76f; }

.uvm-card-label { font-size: 15px; font-weight: 700; color: var(--dk-text-primary, #0D3349); margin-bottom: 4px; }
.uvm-card-hint { font-size: 12px; color: var(--dk-text-faint, #aab0bc); margin-bottom: 14px; }
.uvm-card-drop-hint { font-size: 11.5px; color: var(--dk-text-faint, #c8d0db); margin-bottom: 14px; }

.uvm-file-info { margin-bottom: 12px; }
.uvm-file-name { font-size: 12px; color: var(--dk-text-primary, #344563); font-weight: 500; word-break: break-all; margin-bottom: 2px; }
.uvm-file-size { font-size: 11px; color: var(--dk-text-faint, #aab0bc); }

.uvm-card-btn {
  display: inline-block; padding: 7px 18px; border-radius: 8px;
  font-size: 13px; font-weight: 600; pointer-events: none;
}
.uvm-btn-select { background: rgba(232,106,16,0.1); color: #E86A10; border: 1px solid rgba(232,106,16,0.2); }
.uvm-btn-change { background: rgba(40,199,111,0.1); color: #1e9957; border: 1px solid rgba(40,199,111,0.2); }

/* ─── Footer ─── */
.uvm-footer { display: flex; justify-content: flex-end; gap: 10px; padding-top: 16px; border-top: 1.5px solid var(--dk-border-light, #f5f6fa); }
.uvm-btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 20px; border-radius: 10px; font-size: 14px; font-weight: 600;
  cursor: pointer; border: none; transition: all 0.15s;
}
.uvm-btn-cancel { background: var(--dk-bg-inset, #f5f6fa); color: var(--dk-text-secondary, #6b7280); }
.uvm-btn-cancel:hover { background: #edf0f3; }
.uvm-btn-upload { background: linear-gradient(135deg, #E86A10, #f08535); color: #fff; box-shadow: 0 4px 12px rgba(232,106,16,0.3); }
.uvm-btn-upload:hover { background: linear-gradient(135deg, #cf5e0d, #E86A10); box-shadow: 0 6px 16px rgba(232,106,16,0.4); }
.uvm-btn-disabled { background: #dde1e8 !important; color: var(--dk-text-faint, #aab0bc) !important; box-shadow: none !important; cursor: not-allowed; }
.uvm-count-badge {
  background: rgba(255,255,255,0.3); color: #fff;
  width: 20px; height: 20px; border-radius: 50%; font-size: 11px;
  display: inline-flex; align-items: center; justify-content: center; font-weight: 700;
}

/* ─── Uploading State ─── */
.uvm-uploading { padding: 36px 32px 40px; }
.uvm-uploading-inner { display: flex; flex-direction: column; align-items: center; gap: 20px; }

/* Animated ring */
.uvm-upload-anim {
  position: relative; width: 72px; height: 72px;
  display: flex; align-items: center; justify-content: center;
}
.uvm-upload-ring {
  position: absolute; inset: 0; border-radius: 50%;
  border: 3px solid var(--dk-border-light, #f5f6fa);
  border-top-color: #E86A10;
  animation: uvm-spin 1s linear infinite;
}
.uvm-upload-arrow { position: relative; z-index: 1; }
@keyframes uvm-spin { to { transform: rotate(360deg); } }

/* Upload status */
.uvm-upload-status { text-align: center; }
.uvm-upload-title { font-size: 16px; font-weight: 700; color: var(--dk-text-primary, #0D3349); }
.uvm-upload-file { font-size: 12.5px; color: var(--dk-text-muted, #8a94a6); margin-top: 4px; word-break: break-all; max-width: 320px; }

/* Progress */
.uvm-progress-wrap { width: 100%; max-width: 400px; }
.uvm-progress-track { height: 8px; background: var(--dk-bg-inset, #f0f2f5); border-radius: 10px; overflow: hidden; }
.uvm-progress-fill {
  height: 100%; border-radius: 10px;
  background: linear-gradient(90deg, #E86A10, #F5B935);
  transition: width 0.3s ease;
}
.uvm-progress-meta { display: flex; justify-content: space-between; margin-top: 8px; }
.uvm-progress-pct { font-size: 14px; font-weight: 700; color: #E86A10; }
.uvm-progress-eta { font-size: 13px; color: var(--dk-text-faint, #aab0bc); }

/* Steps */
.uvm-steps { display: flex; flex-direction: column; gap: 10px; width: 100%; max-width: 340px; }
.uvm-step { display: flex; align-items: center; gap: 10px; }
.uvm-step-dot {
  width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0;
  border: 2px solid #dde1e8; background: var(--dk-bg-card, #fff);
  display: flex; align-items: center; justify-content: center;
  transition: all 0.3s;
}
.uvm-step span { font-size: 13px; color: var(--dk-text-faint, #aab0bc); font-weight: 500; }
.uvm-step.uvm-step-active .uvm-step-dot { border-color: #E86A10; background: rgba(232,106,16,0.1); animation: uvm-pulse 1.5s ease infinite; }
.uvm-step.uvm-step-active span { color: #E86A10; font-weight: 600; }
.uvm-step.uvm-step-done .uvm-step-dot { border-color: #28c76f; background: #28c76f; color: #fff; }
.uvm-step.uvm-step-done span { color: #28c76f; }
@keyframes uvm-pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(232,106,16,0.3); } 50% { box-shadow: 0 0 0 6px rgba(232,106,16,0); } }

@media (max-width: 520px) {
  .uvm-cards { grid-template-columns: 1fr; }
  .uvm-modal { border-radius: 16px 16px 0 0; margin-top: auto; }
  .uvm-overlay { align-items: flex-end; }
}
</style>
